/* Magic Layers — client-side subject cut-out via BodyPix running in a Web Worker */

let worker = null;
let currentParam; // undefined = not spawned, null = auto (webgl), "wasm", "cpu"
let seq = 0;
const pending = new Map();
let bootPromise = null;
let stageCb = null;
let provenParam; // sticky backend param once a segmentation succeeds

function killWorker() {
  if (worker) {
    try { worker.terminate(); } catch (e) {}
    worker = null;
  }
  currentParam = undefined;
  pending.forEach((p) => p.reject(new Error("cancelled")));
  pending.clear();
}

function ensureWorker(param) {
  if (worker && currentParam === param) return;
  killWorker();
  currentParam = param;
  let bootResolve;
  bootPromise = new Promise((resolve, reject) => {
    bootResolve = resolve;
    setTimeout(() => reject(new Error("AI engine failed to load — a network filter or ad-blocker may be blocking it")), 25000);
  });
  worker = new Worker("/magic-worker.js?v=4" + (param ? `&backend=${param}` : ""));
  worker.onmessage = (e) => {
    const d = e.data;
    if (d.type === "booted") { bootResolve(); return; }
    if (d.type === "stage") { if (stageCb) stageCb(d.stage, d.backend); return; }
    const { id, mask, error } = d;
    const p = pending.get(id);
    if (!p) return;
    pending.delete(id);
    if (error) p.reject(new Error(error));
    else p.resolve(mask);
  };
  worker.onerror = (e) => {
    pending.forEach((p) => p.reject(new Error(e.message || "Worker failed to load")));
    pending.clear();
    worker = null;
    currentParam = undefined;
  };
}

async function trySegment(segData, sw, sh, param, timeoutMs) {
  ensureWorker(param);
  await bootPromise;
  const id = ++seq;
  return await new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    const copy = new Uint8ClampedArray(segData.data);
    worker.postMessage({ id, data: copy.buffer, width: sw, height: sh }, [copy.buffer]);
    setTimeout(() => {
      if (pending.has(id)) {
        pending.delete(id);
        reject(new Error("segment-timeout"));
      }
    }, timeoutMs);
  });
}

const fileToImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not read that image"));
    img.src = url;
  });

const srcToImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load subject image"));
    img.src = src;
  });

/* Feather the cut-out's edges: blur its alpha channel, clipped to the sharp silhouette */
export async function featherSubject(src, radius) {
  const img = await srcToImage(src);
  const c = document.createElement("canvas");
  c.width = img.width;
  c.height = img.height;
  const ctx = c.getContext("2d");
  ctx.drawImage(img, 0, 0);
  if ("filter" in ctx) {
    ctx.globalCompositeOperation = "destination-in";
    ctx.filter = `blur(${radius}px)`;
    ctx.drawImage(img, 0, 0);
  } else {
    const id = ctx.getImageData(0, 0, c.width, c.height);
    blurAlpha(id, c.width, c.height, radius);
    ctx.putImageData(id, 0, 0);
  }
  return c.toDataURL("image/png");
}

function blurAlpha(id, w, h, radius) {
  const d = id.data;
  const orig = new Uint8ClampedArray(w * h);
  for (let i = 0; i < w * h; i++) orig[i] = d[i * 4 + 3];
  let a = orig.slice();
  const r = Math.max(1, Math.round(radius / 2));
  for (let pass = 0; pass < 2; pass++) {
    const b = new Uint8ClampedArray(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sum = 0, n = 0;
        for (let k = -r; k <= r; k++) {
          const xx = x + k;
          if (xx >= 0 && xx < w) { sum += a[y * w + xx]; n++; }
        }
        b[y * w + x] = sum / n;
      }
    }
    const b2 = new Uint8ClampedArray(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sum = 0, n = 0;
        for (let k = -r; k <= r; k++) {
          const yy = y + k;
          if (yy >= 0 && yy < h) { sum += b[yy * w + x]; n++; }
        }
        b2[y * w + x] = sum / n;
      }
    }
    a = b2;
  }
  for (let i = 0; i < w * h; i++) d[i * 4 + 3] = Math.min(orig[i], a[i]);
}

export async function magicCut(file, onStage) {
  stageCb = onStage || null;
  const img = await fileToImage(file);
  const MAX = 1280;
  const s = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.round(img.naturalWidth * s);
  const h = Math.round(img.naturalHeight * s);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const src = canvas.toDataURL("image/jpeg", 0.92);

  // segment on a small copy — much faster on every backend, mask upscaled after
  const SEG_MAX = 512;
  const ss = Math.min(1, SEG_MAX / Math.max(w, h));
  const sw = Math.max(1, Math.round(w * ss));
  const sh = Math.max(1, Math.round(h * ss));
  const segCanvas = document.createElement("canvas");
  segCanvas.width = sw;
  segCanvas.height = sh;
  const segCtx = segCanvas.getContext("2d");
  segCtx.drawImage(canvas, 0, 0, sw, sh);
  const segData = segCtx.getImageData(0, 0, sw, sh);

  /* Backend ladder with watchdog: auto(webgl) → wasm → cpu.
     A hung GPU driver can stall tfjs forever — on timeout we kill the worker and retry
     with a more compatible engine. Once one succeeds it's remembered for the session. */
  const ladder = provenParam !== undefined ? [provenParam] : [null, "wasm", "cpu"];
  let mask = null;
  let lastErr = null;
  for (let t = 0; t < ladder.length; t++) {
    const param = ladder[t];
    try {
      mask = await trySegment(segData, sw, sh, param, param === "cpu" ? 60000 : 30000);
      provenParam = param;
      break;
    } catch (err) {
      lastErr = err;
      killWorker();
      const next = ladder[t + 1];
      if (next !== undefined && stageCb) stageCb("retry", next || "wasm");
    }
  }
  if (!mask) {
    provenParam = undefined;
    if (lastErr && !/segment-timeout|cancelled/.test(lastErr.message)) throw lastErr;
    throw new Error("Background removal stalled on this device — please try once more, or use a different browser");
  }

  let subjectPixels = 0;
  for (let i = 0; i < mask.length; i++) if (mask[i]) subjectPixels++;
  if (subjectPixels < mask.length * 0.005) {
    throw new Error("No person detected in this photo — Magic Layers needs a visible subject");
  }

  // upscale mask to full size with smoothing for soft anti-aliased edges
  const mCanvas = document.createElement("canvas");
  mCanvas.width = sw;
  mCanvas.height = sh;
  const mCtx = mCanvas.getContext("2d");
  const mImg = mCtx.createImageData(sw, sh);
  for (let i = 0; i < mask.length; i++) mImg.data[i * 4 + 3] = mask[i] ? 255 : 0;
  mCtx.putImageData(mImg, 0, 0);
  const uCanvas = document.createElement("canvas");
  uCanvas.width = w;
  uCanvas.height = h;
  const uCtx = uCanvas.getContext("2d");
  uCtx.imageSmoothingEnabled = true;
  uCtx.imageSmoothingQuality = "high";
  uCtx.drawImage(mCanvas, 0, 0, w, h);
  const up = uCtx.getImageData(0, 0, w, h).data;

  const out = ctx.getImageData(0, 0, w, h);
  for (let i = 0; i < w * h; i++) {
    const a = up[i * 4 + 3];
    if (a < out.data[i * 4 + 3]) out.data[i * 4 + 3] = a;
  }
  const cut = document.createElement("canvas");
  cut.width = w;
  cut.height = h;
  cut.getContext("2d").putImageData(out, 0, 0);
  return { src, subjectSrc: cut.toDataURL("image/png"), w, h };
}
